'use client';

import { useEffect, useRef } from 'react';

/**
 * `useExitIntent` — fires `onTrigger` exactly once when the user's
 * mouse leaves through the TOP edge of the viewport (the classical
 * "I'm reaching for the close button / URL bar" signal).
 *
 * F-500 defaults intentionally conservative — every guard is here
 * because a mis-fired exit-intent modal feels like spam:
 *
 *   - Pointer devices only. Touch and stylus never trigger (no
 *     mouse-leave semantics → false positives on iOS rubber-band).
 *   - Reduced-motion users opted out — same group that disables
 *     marketing flourishes; respect the signal.
 *   - Throttled via `sessionStorage` so a user who closes the modal
 *     once doesn't see it again until they reopen the tab.
 *   - Engagement gate: don't fire before `minScrollPx` (default 600).
 *     Users who never engaged with the page won't appreciate the
 *     intercept.
 *   - Warm-up delay so the modal can't fire in the first second
 *     after mount (avoids the "shows immediately when you land"
 *     UX bug from quick cursor flicks).
 *   - The actual trigger requires `e.clientY <= 0` (mouse above
 *     viewport, not just outside it laterally).
 *
 * The hook intentionally returns nothing — `onTrigger` is the only
 * side-effect contract. Cleanup is automatic on unmount.
 */

interface UseExitIntentOptions {
  /** Master switch — set to false to disable (e.g. on storefront, admin). */
  enabled: boolean;
  /** Called once per session when intent is detected. */
  onTrigger: () => void;
  /**
   * Session-storage key. Use a distinct key when running multiple
   * exit-intent variants concurrently.
   */
  sessionKey?: string;
  /**
   * Don't fire until the user has scrolled at least this many pixels.
   * 0 disables the gate.
   */
  minScrollPx?: number;
  /**
   * Don't fire within this many ms after mount — avoids edge-flick
   * false positives.
   */
  warmupMs?: number;
}

const DEFAULT_SESSION_KEY = 'eazybooking:exit-intent-fired';
const DEFAULT_MIN_SCROLL = 600;
const DEFAULT_WARMUP_MS = 1500;

export function useExitIntent({
  enabled,
  onTrigger,
  sessionKey = DEFAULT_SESSION_KEY,
  minScrollPx = DEFAULT_MIN_SCROLL,
  warmupMs = DEFAULT_WARMUP_MS,
}: UseExitIntentOptions) {
  // Latest callback ref so the listener closure can stay stable
  // across re-renders without re-binding.
  const triggerRef = useRef(onTrigger);
  useEffect(() => {
    triggerRef.current = onTrigger;
  }, [onTrigger]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    // Already fired this session — bail without binding anything.
    try {
      if (sessionStorage.getItem(sessionKey) === '1') return;
    } catch {
      // sessionStorage disabled (Safari Private Mode) — proceed.
      // Worst case: modal can re-show on hard refresh.
    }

    // Pointer-device check. `(pointer: fine)` matches mouse / trackpad
    // with hover; excludes touch. CSS media query is the canonical way
    // (matches what Tailwind's `hover-hover` variant does).
    const hasFineMouse =
      window.matchMedia?.('(hover: hover) and (pointer: fine)').matches ?? false;
    if (!hasFineMouse) return;

    // Reduced-motion: same audience opts out of marketing nudges.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const mountedAt = Date.now();
    let fired = false;

    const handleMouseOut = (e: MouseEvent) => {
      if (fired) return;
      if (Date.now() - mountedAt < warmupMs) return;
      if (minScrollPx > 0 && window.scrollY < minScrollPx) return;

      // mouseout fires on every element transition; we only care
      // about exiting the document toward the top edge.
      //   - `relatedTarget` null means the cursor left the page.
      //   - clientY <= 0 means the exit edge is the top.
      // Together this is the standard exit-intent fingerprint.
      if (e.relatedTarget !== null) return;
      if (e.clientY > 0) return;

      fired = true;
      try {
        sessionStorage.setItem(sessionKey, '1');
      } catch {
        /* ignored — guarded above */
      }
      triggerRef.current();
    };

    document.addEventListener('mouseout', handleMouseOut);
    return () => document.removeEventListener('mouseout', handleMouseOut);
  }, [enabled, sessionKey, minScrollPx, warmupMs]);
}
